import {
  type DefaultError,
  type Query,
  QueryCache,
  QueryClient,
} from "@tanstack/react-query";

import { ApiError } from "@/lib/api/error";
import { ForbiddenApiError } from "@/lib/api/error.forbidden";
import { UnauthorizedApiError } from "@/lib/api/error.unauthorized";
import { ERROR_EVENTS } from "@/lib/events/constants";
import { emitEvent } from "@/lib/events/helpers";
import { getLogger } from "@/lib/logger";

/**
 * @see https://tanstack.com/query/latest/docs/framework/react/typescript#typing-meta
 */
interface ReactQueryMeta extends Record<string, unknown> {
  /**
   * クエリのエラー時にグローバルエラーダイアログを表示しない
   */
  disableGlobalErrorHandling?: boolean;
}

declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: ReactQueryMeta;
    mutationMeta: ReactQueryMeta;
  }
}

/**
 * 無限スクロールのクエリを識別するための一意な識別子。
 * queryKey にセットすることで、無限スクロールのクエリであることを示す。
 * 他のクエリキーと衝突しないように、ユニークな識別子を使用する。
 */
export const INFINITE_QUERY_KEY_SUFFIX = `infinite-query-${crypto.randomUUID()}`;

export const handleSystemApiError = (props: {
  error: Error | undefined;
  isHistoryBackIfPossible?: boolean;
}) => {
  const { error, isHistoryBackIfPossible = false } = props;

  if (error instanceof ForbiddenApiError) {
    // イベントベースでForbiddenApiErrorDialogを表示
    emitEvent(ERROR_EVENTS.FORBIDDEN_API, {
      accessibles: error.accessibles,
      action: "show",
    });
    return;
  }
  if (error instanceof UnauthorizedApiError) {
    // ログアウトされるのでエラーダイアログを表示しない
    return;
  }
  if (error instanceof ApiError) {
    // イベントベースでGlobalErrorDialogを表示
    emitEvent(ERROR_EVENTS.GLOBAL, {
      description: error.getResponse().displayMessage,
      isHistoryBackIfPossible,
      action: "show",
    });
    return;
  }
  // NOTE: API用のエラーダイアログも用意していいかも
  // if (error instanceof InternalServerApiError) {}

  // イベントベースでGlobalErrorDialogを表示（汎用エラー）
  emitEvent(ERROR_EVENTS.GLOBAL, {
    isHistoryBackIfPossible,
    action: "show",
  });
};

/**
 * Mutationのエラーハンドリング関数
 */
const defaultMutationOnError = (error?: Error) => {
  getLogger().error("Error in mutation", error);
  return handleSystemApiError({ error });
};

/**
 * Queryのエラーハンドリング関数
 */
const defaultQueryOnError = (
  error: DefaultError,
  query: Query<unknown, unknown, unknown>,
) => {
  getLogger().error("Error in query cache", { error, query });
  // metaでグローバルエラーハンドリングを無効化している場合はスキップ
  if (query.meta?.disableGlobalErrorHandling) {
    return;
  }
  return handleSystemApiError({ error });
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // 401, 403はリトライしない
        if (error instanceof UnauthorizedApiError) {
          return false;
        }
        if (error instanceof ForbiddenApiError) {
          return false;
        }
        return failureCount < 3;
      },
    },
    mutations: {
      onError: defaultMutationOnError,
    },
  },
  queryCache: new QueryCache({
    /**
     * useQuery()のオプションにはonErrorが存在しないため、`defaultOptions.queries.onError`は使用できない
     * @see https://tkdodo.eu/blog/breaking-react-querys-api-on-purpose
     */
    onError: defaultQueryOnError,
  }),
});

/**
 * Mutationのエラーハンドリング関数
 * @note {@link MutateOptions.onError}から引用
 */
type OnErrorFn<TError, TVariables, TContext> = (
  error: TError,
  variables: TVariables,
  context: TContext | undefined,
) => unknown;

/**
 * `onError`で発生したエラーをグローバルエラーハンドリングに委譲する
 * @note {@link MutateOptions.onError}から引用
 */
export function withGlobalErrorHandling<
  TError = DefaultError,
  TVariables = void,
  TContext = unknown,
>(
  fn: OnErrorFn<TError, TVariables, TContext>,
): OnErrorFn<TError, TVariables, TContext> {
  return (error, variables, context) => {
    try {
      return fn(error, variables, context);
    } catch (error) {
      defaultMutationOnError();
      throw error;
    }
  };
}
